import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { getMccCategoryTKey } from "../../constants/mccCategories.constants";
import { OperationType, TransactionStatus } from "../../types/card-movements.types";
import type { CardStatementTransaction } from "../../types/card-statement.types";
import { formatTransactionAmount, getTransactionTypeMeta } from "../../utils/cardTransactionDetailHelpers";
import { resolveUsdAmount, shouldShowLocalAmount } from "../../utils/transactionAmountFormat";

export interface StatementMovementRowProps {
  transaction: CardStatementTransaction;
}

const STATUS_BADGE_TKEY: Partial<Record<TransactionStatus, string>> = {
  FAILED: "statement_badge_declined",
  PENDING: "statement_badge_pending",
  REVERSED: "statement_badge_reversed",
};

const localeFor = (lang: string) => (lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US");

const formatShortDateTime = (raw: string, lang: string) => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const locale = localeFor(lang);
  return `${date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })} ${date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
};

export const StatementMovementRow = ({ transaction: tx }: StatementMovementRowProps) => {
  const { t, activeLanguage } = useTranslations("cards");

  const usdAmount = resolveUsdAmount(tx);
  const showLocal = shouldShowLocalAmount(tx);
  const isCredit = tx.operation_type === OperationType.CREDIT;
  const isFailed = tx.status === TransactionStatus.FAILED;
  const sign = isCredit ? "+" : "−";

  const categoryTKey = getMccCategoryTKey(tx.merchant_category_code);
  const category = categoryTKey ? t(categoryTKey as never) : (tx.merchant_category_code ?? "—");

  const badgeTKey = STATUS_BADGE_TKEY[tx.status];

  const headline =
    usdAmount != null
      ? `${sign}${formatTransactionAmount(Math.abs(usdAmount), "USD", activeLanguage)}`
      : `${sign}${formatTransactionAmount(Math.abs(tx.amount), tx.currency, activeLanguage)}`;

  return (
    <tr className="statement-row border-b border-border last:border-b-0">
      <td className="pr-2 font-mono text-[9px] leading-[13px] tabular-nums text-muted-foreground">
        {formatShortDateTime(tx.created_at, activeLanguage)}
      </td>
      <td className="pr-2 font-mono text-[9px] leading-[13px] tabular-nums text-muted-foreground">
        {tx.reference}
      </td>
      {/* Badge stays on the description line: a second line would break the fixed row height the page
          chunking depends on. */}
      <td className="max-w-0 pr-2 text-[10px] leading-[13px] text-foreground">
        <span className="flex items-baseline gap-1.5">
          <span className="truncate">{tx.merchant_name || tx.description || "—"}</span>
          {badgeTKey && (
            <span className="shrink-0 rounded-sm bg-muted px-1 text-[8px] font-medium uppercase tracking-wide text-muted-foreground">
              {t(badgeTKey as never)}
            </span>
          )}
        </span>
      </td>
      <td className="max-w-0 pr-2 text-[9px] leading-[13px] text-muted-foreground">
        <span className="block truncate">{category}</span>
      </td>
      <td className="max-w-0 pr-2 text-[9px] leading-[13px] text-muted-foreground">
        <span className="block truncate">{t(getTransactionTypeMeta(tx.transaction_type).tKey as never)}</span>
      </td>
      <td className="pl-2 text-right">
        <span
          className={cn(
            "block font-mono text-[10px] font-medium leading-[13px] tabular-nums",
            isCredit ? "text-[var(--success)]" : "text-foreground",
            isFailed && "text-muted-foreground line-through",
          )}
        >
          {headline}
        </span>
        {showLocal && (
          <span className="block font-mono text-[8px] leading-[10px] tabular-nums text-muted-foreground">
            {formatTransactionAmount(Math.abs(tx.amount), tx.currency, activeLanguage)}
          </span>
        )}
      </td>
    </tr>
  );
};
